import { Button, Icon, Menu, Modal } from '@restomarker/ui-kit';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState, type ReactElement } from 'react';

import { type CurrentUser } from '@/modules/auth';
import {
  AlertsNavLink,
  NotificationsDialog,
  useAlertFeedAvailable,
  useNotificationsAvailable,
} from '@/modules/notifications';
import { isSupportSession } from '@/shared/auth';
import { useLogout } from '@/shared/session';
import { setViewMode } from '@/shared/storage';
import { t } from '@/shared/i18n';

import styles from './appNav.module.css';

export interface AppNavProps {
  user: CurrentUser;
}

/**
 * Навигация кабинета.
 *
 * До неё в приложении было построено девять экранов, не связанных между собой ничем:
 * ни одной ссылки, ни одного `<nav>`, ни кнопки выхода. Менеджер приземлялся на меню
 * точки и не мог попасть на дашборд мониторинга — на свой стартовый экран
 * по спецификации, — а владелец сети не видел ни одного экрана мониторинга вообще,
 * хотя `roles.md` называет его ЛПР по деньгам.
 *
 * Состав пунктов зависит от роли. Скрытие пункта — ТОЛЬКО удобство: доступ закрывают
 * `staticData.allowedRoles` роута и права на сервере (app.md §7). Спрятать и не закрыть
 * значит не закрыть.
 */
export interface AppNavProps2 {
  /** Куда `PageHeader` перенесёт действие страницы на узком экране. */
  onActionSlot?: (node: HTMLElement | null) => void;
}

export function AppNav({ user, onActionSlot }: AppNavProps & AppNavProps2): ReactElement {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const [menuOpen, setMenuOpen] = useState(false);

  // `includes` через приведение: у литерального кортежа ролей тип уже сузился
  // до конкретных строк, и TypeScript не даёт спросить его о роли из общего union.
  const items = NAV_ITEMS.filter((item) => (item.roles as readonly string[]).includes(user.role));
  // Текущий раздел — самый длинный совпавший путь: карточка сети
  // `/platform/tenants/…` принадлежит «Сетям», а не «Аналитике» на `/platform`.
  const current = items
    .filter((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];

  // Выход идёт ЧЕРЕЗ `useLogout`, а не мимо него. Раньше кнопка звала `clearToken()`
  // напрямую, и вся продуманная логика выхода — очистка кэша запросов и ключей
  // Preferences — не исполнялась ни разу: `useLogout` был мёртвым кодом. Стоило это
  // меню предыдущего человека, всплывающего у следующего как своё (риск Р5).
  // Правило из `offline-first.md` §3: выход — ОДНА функция, вызываемая отовсюду.
  const logout = useLogout();
  const navigate = useNavigate();
  // Выход через ПОДТВЕРЖДЕНИЕ, как и в киоске. Кнопка стоит в общем ряду с разделами,
  // и промах по ней стоит дорого несимметрично: вернуться можно только паролем,
  // а без сети — никак. Раньше подтверждение было у сотрудника и не было у остальных,
  // хотя цена ошибки у них та же.
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const [telegramOpen, setTelegramOpen] = useState(false);
  // Пункт прячется, когда ни бот Telegram, ни push не настроены на установке:
  // предлагать подключение, которое не может состояться, — обещание вхолостую.
  const telegramAvailable = useNotificationsAvailable();
  // Лента точки (T-047) — разделом у менеджера и владельца сети: у платформы своей точки
  // нет. Пункт меню аккаунта при этом зовётся «Настроить уведомления», чтобы в кабинете
  // не было двух одинаковых «Уведомлений».
  const feedAvailable = useAlertFeedAvailable();
  const showFeed =
    feedAvailable && (user.role === 'client_admin' || user.role === 'location_manager');

  // Переключатель есть у КАЖДОЙ роли, а не только у сотрудника. Владелец сети и шеф
  // заходят посмотреть, как выглядит кухонный экран, — и раньше это был единственный
  // способ туда попасть: киоск жил отдельной системой входа. Теперь это вид, и вход
  // в него ничем не отличается от переключения вкладки.
  async function enterKiosk(): Promise<void> {
    await setViewMode('kiosk');
    await navigate({ to: '/kiosk/print' });
  }

  // Настройка принтера — ПРЯМО из кабинета, а не только через вид киоска (2026-09-05).
  // Раньше единственный путь был «Кухня» → имя в шапке киоска → «Настройка принтера» —
  // четыре тапа и два разных меню ради одного экрана, который к самой печати
  // не имеет отношения (D2: привязка принтера — локальные данные устройства,
  function handleLogout(): void {
    setConfirmingLogout(false);
    // Токен стирается ДАЖЕ если сервер не ответил (`onSettled`, а не `onSuccess`):
    // человек нажал «Выйти» и обязан выйти. Оставить локальный токен из-за сетевой
    // ошибки значило бы держать открытым кабинет на общем моноблоке.
    //
    // Уход РОУТЕРОМ, а не `window.location.assign`. Полная перезагрузка без сети
    // не проходит: приложение получает белый экран и не восстанавливается до появления
    // связи. Сценарий штатный — смена кончилась, повар выходит, WiFi лежит, — и цена
    // ошибки была бы мёртвый моноблок на кухне.
    //
    // Перезагрузка страховала «вдруг что-то осталось в памяти», но страховать нечего:
    // токен, ключи Preferences, профиль на диске и кэш запросов чистятся явно
    // в `useLogout`. Найдено E2E-AUTH-004.
    // В саппорт-сессии «Выйти» возвращает в СВОЮ сессию платформы (T-039): `clearToken`
    // там гасит только чужой токен, и экран входа был бы ложью — вход не нужен.
    const wasSupport = isSupportSession();
    logout.mutate(undefined, {
      onSettled: () => {
        if (wasSupport) {
          void navigate({ to: '/platform' });
          return;
        }
        void navigate({ to: '/login', search: { redirect: undefined } });
      },
    });
  }

  return (
    <header className={styles.bar}>
      {/* Бургер — только на узком экране. Восемь разделов, вываленных списком,
          занимали на телефоне пол-экрана до содержимого: навигация вытесняла то,
          ради чего её открыли. */}
      <button
        type="button"
        className={styles.burger}
        aria-expanded={menuOpen}
        aria-controls="app-nav"
        onClick={() => setMenuOpen((value) => !value)}
      >
        <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span>
        <span className={styles.burgerLabel}>{current ? t(current.label) : t('nav.label')}</span>
      </button>

      <nav
        id="app-nav"
        className={menuOpen ? styles.navOpen : styles.nav}
        aria-label={t('nav.label')}
      >
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={item === current ? styles.linkActive : styles.link}
            // aria-current — не украшение: без него скринридер не отличает текущий
            // раздел от остальных, а подсветка ему не видна.
            aria-current={item === current ? 'page' : undefined}
            onClick={() => setMenuOpen(false)}
          >
            {t(item.label)}
          </Link>
        ))}

        {showFeed ? (
          <AlertsNavLink
            className={styles.link}
            activeClassName={styles.linkActive}
            onNavigate={() => setMenuOpen(false)}
          />
        ) : null}

        {/* Настройка принтера ЗДЕСЬ НЕТ намеренно (владелец, 2026-09-08).
            Пункт стоял в этой полосе с 2026-09-05, когда решение было обратным
            («должны быть все вверху»). Пересмотрено: принтер подключают к устройству
            точки, а кабинет открывают с ноутбука, где принтера нет вовсе, — пункт
            уводил в кухонный режим из раздела, где он не нужен. Настройка живёт
            в полосе киоска (`KioskNav`), рядом с печатью, которой она служит. */}

        {/* Аккаунт внутри меню — только на узком экране (CSS прячет секцию на широком).
            Наверху его место занимает действие страницы: имя смотрят раз в смену,
            а «Добавить пользователя» нажимают затем, зачем и открыли экран. */}
        <div className={styles.navAccount}>
          <span className={styles.navAccountName}>{user.full_name}</span>
          <button
            type="button"
            className={styles.navAccountItem}
            onClick={() => {
              setMenuOpen(false);
              void enterKiosk();
            }}
          >
            {t('nav.kioskView')}
          </button>
          {telegramAvailable ? (
            <button
              type="button"
              className={styles.navAccountItem}
              onClick={() => {
                setMenuOpen(false);
                setTelegramOpen(true);
              }}
            >
              {t('nav.telegram')}
            </button>
          ) : null}
          <button
            type="button"
            className={styles.navAccountItem}
            onClick={() => {
              setMenuOpen(false);
              setConfirmingLogout(true);
            }}
          >
            {t('nav.logout')}
          </button>
        </div>
      </nav>

      {/* Слот, в который страница переносит своё главное действие на узком экране.
          Живёт здесь, а сама кнопка приезжает порталом из `PageHeader`: кнопка одна
          и та же, а не копия, иначе в дереве доступности их оказалось бы две. */}
      <div className={styles.pageAction} ref={onActionSlot} />

      {/* Аккаунт одной точкой входа, а не тремя элементами в ряд.
       *
       * Прежде справа стояли «Вид киоска», имя и «Выйти»: два РЕДКИХ действия
       * (в киоск заглядывают проверить, выходят раз в смену) занимали самое дорогое
       * место экрана постоянно и были обведены наравне с рабочими кнопками. Имя при
       * этом было мёртвым текстом, а на узком экране пропадало вовсе — то есть
       * с телефона нельзя было узнать, под кем вошли.
       *
       * Имя, а не кружок с инициалами: кружок не отвечает на вопрос «кто я здесь»,
       * ради которого сюда и смотрят. */}
      <Menu
        triggerClassName={styles.account}
        trigger={
          <>
            <span className={styles.userName}>{user.full_name}</span>
            <Icon name="chevronDown" size={16} />
          </>
        }
        items={[
          {
            key: 'kiosk',
            label: t('nav.kioskView'),
            onSelect: () => {
              void enterKiosk();
            },
          },
          ...(telegramAvailable
            ? [
                {
                  key: 'telegram',
                  label: t('nav.telegram'),
                  onSelect: () => {
                    setTelegramOpen(true);
                  },
                },
              ]
            : []),
          {
            key: 'logout',
            label: t('nav.logout'),
            // Отбито чертой: выход — действие другого рода, чем смена вида,
            // и соседство вплотную делает промах по нему слишком дешёвым.
            separated: true,
            onSelect: () => {
              setConfirmingLogout(true);
            },
          },
        ]}
      />
      {telegramOpen ? <NotificationsDialog onClose={() => setTelegramOpen(false)} /> : null}
      {confirmingLogout ? (
        <Modal
          open
          onOpenChange={() => setConfirmingLogout(false)}
          title={t('kiosk.logout.title')}
          description={t('kiosk.logout.description')}
          footer={
            <>
              <Button onClick={() => setConfirmingLogout(false)}>{t('common.cancel')}</Button>
              <Button variant="primary" disabled={logout.isPending} onClick={handleLogout}>
                {t('nav.logout')}
              </Button>
            </>
          }
        />
      ) : null}
    </header>
  );
}

/**
 * Пункты в порядке частоты использования, а не алфавита.
 *
 * Мониторинг первый у менеджера: это его стартовый экран и то, ради чего он открывает
 * кабинет посреди смены. Справочники ниже — их ведут раз в месяц.
 */
// Печати здесь НЕТ: она живёт только в виде киоска (решение владельца 2026-07-30).
//
// Кабинет — это управление: меню, сроки, персонал, разбор журнала. Печать — работа
// у стола, и вести на неё из кабинетного меню значит предлагать менеджеру делать
// то, что делают в цехе. Кому надо — переключается в вид киоска кнопкой рядом.
const NAV_ITEMS = [
  // Раздел платформы первым (T-038): её стартовый экран и единственный, где видны
  // сразу все клиенты.
  //
  // Кабинетных разделов у платформы НЕТ (T-039): данные сети она смотрит только
  // из саппорт-сессии — «Войти как…» в аналитике, с причиной и записью в аудите.
  //
  // «Эталонный каталог» (/catalog/master) и «Релизы приложений» (/platform/releases)
  // из меню убраны (решение владельца 2026-10-14): платформенную учётку получает
  // команда, которой нужны только клиенты и статистика. Страницы живы и открываются
  // по прямому адресу — их ведёт разработка.
  { to: '/platform', label: 'nav.analytics', roles: ['platform_admin'] },
  { to: '/platform/tenants', label: 'nav.tenantsAdmin', roles: ['platform_admin'] },
  { to: '/stats', label: 'nav.stats', roles: ['platform_admin'] },
  // Печать первой у сотрудника: это единственное, ради чего он открывает приложение,
  // и до T-018 её в кабинетной навигации не было вовсе — у него был ОДИН пункт,
  // «Меню точки», то есть справочник вместо работы. Двух третей объявленного
  // минимума (решение Д5: меню, печать, свои печати) в меню просто не существовало.
  {
    to: '/monitoring',
    label: 'nav.monitoring',
    roles: ['client_admin', 'location_manager'],
  },
  // Статистика (T-057) — между «что горит сейчас» и «что здесь произошло»: она
  // отвечает «сколько сделали за период».
  {
    to: '/stats',
    label: 'nav.stats',
    roles: ['client_admin', 'location_manager'],
  },
  {
    to: '/journal',
    label: 'nav.journal',
    roles: ['client_admin', 'location_manager'],
  },
  {
    to: '/catalog/menu',
    label: 'nav.menu',
    roles: ['client_admin', 'location_manager', 'employee'],
  },
  {
    to: '/catalog/master',
    label: 'nav.masterCatalog',
    roles: ['client_admin'],
  },
  {
    to: '/catalog/categories',
    label: 'nav.categories',
    roles: ['client_admin', 'location_manager'],
  },
  {
    to: '/staff',
    label: 'nav.staff',
    roles: ['client_admin', 'location_manager'],
  },
  // Менеджеру точки — тоже (T-040): здесь список планшетов, закреплённых за его точкой,
  // и «Открепить». Пропавший планшет закрывают сразу, а не звонком владельцу сети.
  // Создавать точки ему по-прежнему нельзя — кнопки на странице для него нет.
  {
    to: '/locations',
    label: 'nav.locations',
    roles: ['client_admin', 'location_manager'],
  },
] as const;
